// AuthFlowContainer.jsx
// This component replaces your original LoginAndMFA.jsx and includes logic for both flows.

import { ThemeProvider } from "@mui/material";
import { useState, useEffect } from "react";
import { setHeaders } from "../redux/slices/authSlice";
import { setLoggedInUser ,setLogoutUser } from "../redux/slices/userSlice";
import WebLogin from "./Login"; // Rename your existing login UI component
import MobileLoginAndMFA from "./mobile/MobileLoginAndMFA"; // The new mobile login UI component
import MFAHandler from "./MFAHandler";
import MobileMFA from "./mobile/MobileMFA";
import { toast } from "react-toastify";
import axios from "axios";
import cryptoJs from "crypto-js";
import { useDispatch, useSelector } from "react-redux";
import ScreenAfterLogin from "./EmmsDashboard";
import theme from "../theme";
import { makeStyles } from "@material-ui/core";

const secretKey = "elm_password_secret_key";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 0,

    display: "flex",
    minWidth: "80vw",
    maxHeight: "100vh",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "stretch",
  },
  paper: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),

    borderRadius: 0,
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      minWidth: "100vw",
    },
    minWidth: "25vw",
    overflow: "hidden",
  },
  form: {
    display: "flex",
    paddingTop: theme.spacing(7),
    paddingLeft: theme.spacing(1.7),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(11),
    flexDirection: "column",
    overflow: "hidden",
    gap: theme.spacing(3),
    [theme.breakpoints.up("xl")]: {
      paddingTop: theme.spacing(17),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
  },
  "logo-login": {
    height: "auto",
    position: "relative",
    overflow: "hidden",
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
  },
  inputLabel: {
    marginBottom: "-1.75em",
    textAlign: "left",
    fontWeight: "bold",
  },
  footer: {
    textAlign: "center",
    position: "fixed",
    fontSize: "10px",
    bottom: "2.5em",
    marginLeft: "1em",

    paddingRight: "2em",

    [theme.breakpoints.up("xl")]: {
      marginLeft: "6%",
    },
  },
  footerLink: {
    color: "black",
    textDecoration: "none",
  },
}));

// You need to pass the 'isMobileFlow' flag to this container
export const AuthFlowContainer = ({ isMobileFlow, mobileClasses, decodedUser, setDecodedUser, handleSetLoggedInUser }) => {
  const [view, setView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginToken, setLoginToken] = useState("");
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [staySignedIn, setStaySignedIn] = useState(false)

  const webClasses = useStyles();
  const classes = isMobileFlow ? mobileClasses : webClasses;
  const dispatch = useDispatch();
  const headers = useSelector((state) => state.header);
  

  const completeLogin = (token, user) => {
    dispatch(setLoggedInUser(user));
    if (staySignedIn || isMobileFlow) {
      localStorage.setItem("user", btoa(JSON.stringify(user)));
      localStorage.setItem("token", token);
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("token");
    }
    else {
      sessionStorage.setItem("user", btoa(JSON.stringify(user)));
      sessionStorage.setItem("token", token);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }

    dispatch(
      setHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      })
    );

    sessionStorage.removeItem("stayWelcomePage")

    setView("home");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const api = localStorage.getItem("api");
    const encryptedPassword = cryptoJs.AES.encrypt(
      password,
      secretKey
    ).toString();

    axios
      .post(`${api}/login`, {
        email,
        password: encryptedPassword,
      })
      .then((response) => {
        const data = response.data;

        if (data.mfaRequired) {
          setEmail(email);
          setLoginToken(data.loginToken);
          const needsSetup = data.mfaSetupRequired === true;
          setMfaSetupRequired(needsSetup);

          // Block MFA setup on mobile - configuration must be done via desktop
          if (needsSetup && isMobileFlow) {
            console.log(
              "MFA setup missing on mobile, directing to desktop setup."
            );
            toast.error(
              "MFA setup is required. Please configure MFA using the desktop application first.",
              {
                position: toast.POSITION.BOTTOM_RIGHT,
              }
            );
            return;
          }

          // Allow MFA verification (OTP) on both mobile and web when already configured
          setView("mfa");
        } else if (data.token && data.user) {
          completeLogin(data.token, data.user);
        } else {
          throw new Error("Invalid login response");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Login failed. Please check your credentials", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
  };


  const LoginComponent = isMobileFlow ? MobileLoginAndMFA : WebLogin;


  useEffect(() => {
    const validateUser = async () => {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("token");
      const api = localStorage.getItem("api");

      if (!storedUser || !storedToken) return;

      try {

        
        const { data } = await axios.get(
          `${api}/user/getUserInfo`,
          {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          }
        );
                    
    
        setDecodedUser(data);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem("user");
          localStorage.removeItem("token");
        }
        dispatch(setLogoutUser());

        console.error("Auth validation failed:", err);
      }
    };

    validateUser();
  }, []);


  return (
    <ThemeProvider theme={theme}>
      {view === "login" && (
        <LoginComponent
          classes={classes}
          email={email}
          password={password}
          setEmail={setEmail}
          setPassword={setPassword}
          handleSubmit={handleSubmit}
          handleSetLoggedInUser={handleSetLoggedInUser}
          decodedUser={decodedUser}
          setDecodedUser={setDecodedUser}
          staySignedIn={staySignedIn}
          setStaySignedIn={setStaySignedIn}
        />
      )}
      {view === "mfa" && (
        <>
          {isMobileFlow ? (
            // Mobile: Only show OTP verification (setup is blocked above)
            <MobileMFA
              email={email}
              loginToken={loginToken}
              onSuccess={completeLogin}
            />
          ) : (
            // Desktop: Show full MFA handler (setup + verification)
            <MFAHandler
              email={email}
              loginToken={loginToken}
              setupRequired={mfaSetupRequired}
              onSuccess={completeLogin}
              classes={classes}
            />
          )}
        </>
      )}
      {view === "home" && <ScreenAfterLogin />}
    </ThemeProvider>
  );
};
